import React from 'react';
import './LegalModal.css';

const PrivacyModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content legal-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Privacy Policy</h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <div className="legal-content">
                    <p className="last-updated">Last Updated: December 30, 2025</p>

                    <section className="legal-section">
                        <h3>1. Who We Are</h3>
                        <p>
                            This Privacy Policy explains how Jeffrey Discs ("the App") collects, uses, and shares information.
                            The App is owned and operated by School 21 LLC dba DoZESQ Imaginarium ("DoZESQ Imaginarium,"
                            "DoZESQ," "we," "us," or "our"). We created this project in collaboration with the Jeffrey Lucas
                            Gurmendi Marshall Foundation (the "Foundation"). The Foundation is not the legal owner or operator
                            of the App.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>2. Plain-English Summary</h3>
                        <ul>
                            <li>Most visitors can browse disc information without an account.</li>
                            <li>If you submit a disc visit, we collect what you provide (text/media/location details) so we can display the journey.</li>
                            <li>We offer "Public" and "Family Only" visibility for submitted memories. Family-only content requires family sign-in.</li>
                            <li>Disc golf course locations and journey markers are public by design.</li>
                            <li>If you subscribe to emails, we use your email to send the updates you asked for, and you can unsubscribe anytime.</li>
                            <li>We do not sell your personal information and do not share it for third-party marketing.</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>3. Information We Collect</h3>

                        <h4>A. If You Browse the Site:</h4>
                        <ul>
                            <li><strong>Technical/log data:</strong> IP address, browser type, device information, timestamps (standard web/server logs)</li>
                            <li><strong>Cookies/local storage:</strong> Essential cookies and storage used for security and basic functionality</li>
                        </ul>

                        <h4>B. When You Submit a Disc Visit:</h4>
                        <ul>
                            <li><strong>Location data:</strong> The disc golf course name and/or coordinates you provide (and related map placement data)</li>
                            <li><strong>Photos and videos:</strong> Any media you choose to upload</li>
                            <li><strong>Text content:</strong> Your thoughts, memories, or messages</li>
                            <li><strong>Visibility setting:</strong> Whether you mark your submission "Public" or "Family Only"</li>
                        </ul>

                        <h4>C. When You Subscribe to Email Updates:</h4>
                        <ul>
                            <li><strong>Email address:</strong> To send you monthly digests and occasional Jeffrey Discs updates</li>
                            <li><strong>Name:</strong> Optional, to personalize emails</li>
                        </ul>

                        <h4>D. When You Sign In (Family Members):</h4>
                        <ul>
                            <li><strong>Account identifiers:</strong> Email and authentication details (handled by AWS Cognito)</li>
                            <li><strong>Name:</strong> For display purposes</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>4. How We Use Information</h3>
                        <p>We use information to:</p>
                        <ul>
                            <li>Operate, maintain, and improve the App</li>
                            <li>Display disc journeys on the map and timeline</li>
                            <li>Review, moderate, and publish submitted content</li>
                            <li>Enforce visibility settings (Public vs. Family Only)</li>
                            <li>Authenticate family members and control access to family-only content</li>
                            <li>Send subscribed email updates (monthly digests and occasional App/news updates)</li>
                            <li>Secure the App, prevent abuse, and troubleshoot issues</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>5. How Information Is Shared and Displayed</h3>

                        <h4>A. Public Content:</h4>
                        <ul>
                            <li>Visible to everyone who visits the App</li>
                            <li>May appear on disc journey pages, maps, and timelines</li>
                            <li>May be shared by visitors outside the App (we do not control downstream sharing)</li>
                        </ul>

                        <h4>B. Family-Only Content:</h4>
                        <ul>
                            <li>Visible only to authenticated family members</li>
                            <li>Protected by login authentication</li>
                            <li>We reserve the right to determine who qualifies as a family member for access purposes</li>
                        </ul>

                        <h4>C. Service Providers:</h4>
                        <p>
                            We use service providers to host the App, store content, send emails, and display maps.
                            These providers process data on our behalf to provide the Services (see Section 10).
                        </p>

                        <h4>D. Legal and Safety:</h4>
                        <p>
                            We may disclose information if we believe it is reasonably necessary to comply with law,
                            protect the rights and safety of DoZESQ, users, or the public, or prevent fraud/abuse.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>6. Location Information</h3>
                        <ul>
                            <li><strong>Disc golf course locations and journey markers are public by design.</strong></li>
                            <li>This is essential to tracking disc journeys.</li>
                            <li>Photos and videos may include embedded metadata (such as location). We may use it to help place disc journeys.</li>
                            <li>Do not submit location information you want to keep private.</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>7. Cookies and Tracking</h3>
                        <p>Jeffrey Discs uses minimal tracking:</p>
                        <ul>
                            <li><strong>Essential cookies/local storage:</strong> For sign-in/session security and basic functionality</li>
                            <li><strong>Security/logging:</strong> Basic server logs for debugging, fraud prevention, and security</li>
                            <li><strong>No ads:</strong> We do not use advertising tracking pixels</li>
                            <li><strong>No third-party marketing:</strong> We do not share your email with third parties for their marketing</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>8. Content Review and Moderation</h3>
                        <p>
                            Submissions (photos, videos, text) may be reviewed by administrators before appearing on the site.
                            During review, your submission is not visible to others (except administrators). We may approve,
                            reject, edit for formatting/clarity, or remove content at our discretion to maintain a respectful
                            memorial environment and to protect the App and community.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>9. Data Storage, Retention, and Security</h3>
                        <ul>
                            <li><strong>Storage:</strong> Data is stored on Amazon Web Services (AWS) in the United States</li>
                            <li><strong>Encryption:</strong> Data transmission uses HTTPS encryption</li>
                            <li><strong>Authentication:</strong> Family sign-in is managed by AWS Cognito</li>
                            <li><strong>Access control:</strong> Only approved administrators can review submissions and manage content</li>
                            <li><strong>Retention:</strong> Memorial content is generally retained long-term to support the purpose of the App, unless removal is requested and appropriate</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>10. Third-Party Services</h3>
                        <p>The App may use third-party services such as:</p>
                        <ul>
                            <li><strong>AWS (Amazon Web Services):</strong> Hosting, data storage, and authentication components</li>
                            <li><strong>Google Maps:</strong> To display disc locations on maps</li>
                            <li><strong>AWS SES:</strong> To send email notifications and updates</li>
                        </ul>
                        <p>
                            These services have their own privacy policies. We do not control their practices.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>11. Your Rights and Choices</h3>
                        <p>You may have the right to:</p>
                        <ul>
                            <li>Request removal of content you submitted</li>
                            <li>Unsubscribe from email updates at any time</li>
                            <li>Request information about personal data we have about you</li>
                            <li>Request deletion of your account or related personal data, subject to memorial and legal considerations</li>
                        </ul>
                        <p>
                            To request deletion of content you submitted, contact us at
                            <a href="mailto:imagine@dozesq.com">imagine@dozesq.com</a> with:
                        </p>
                        <ul>
                            <li>The disc number</li>
                            <li>The approximate date of submission</li>
                            <li>A description of the content to be deleted</li>
                        </ul>
                        <p>
                            We may ask for additional verification to confirm you are the original submitter.
                            We will review requests on a case-by-case basis.
                        </p>
                        <p>
                            For visitors in certain regions (including the EU/UK), our legal bases for processing may include
                            legitimate interests in operating and securing the service, and consent when you choose to submit
                            content or subscribe to emails. To exercise your rights, contact us at{' '}
                            <a href="mailto:imagine@dozesq.com">imagine@dozesq.com</a>.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>12. Children's Privacy</h3>
                        <p>
                            The App is not directed to children under 13, and we do not knowingly collect personal information
                            from children under 13. If you believe a child has provided personal information, please contact us
                            and we will take appropriate steps to remove it.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>13. Changes to This Policy</h3>
                        <p>
                            We may update this Privacy Policy from time to time. The "Last Updated" date at the top indicates when
                            the policy was last revised. Continued use of the App after changes constitutes acceptance of the
                            updated policy.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>14. Contact Us</h3>
                        <p>
                            Questions about privacy? Contact us at:{' '}
                            <a href="mailto:imagine@dozesq.com">imagine@dozesq.com</a>.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PrivacyModal;
